import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Stack,
  Alert,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Fade,
  Zoom,
  Collapse,
  LinearProgress,
  Tooltip,
  Badge,
  alpha,
  Skeleton,
  Divider
} from '@mui/material';
import {
  AppRegistration as RegisterIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  AutoAwesome as SparkleIcon,
  Rocket as RocketIcon,
  Security as ShieldIcon,
  Speed as BoltIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Add as AddIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  ChevronRight as ArrowIcon,
  AccessTime as TimeIcon,
  ShowChart as ChartIcon
} from '@mui/icons-material';

// Styles object
const styles = {
  mainContainer: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0, 212, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(147, 51, 234, 0.1) 0%, transparent 50%)',
      pointerEvents: 'none'
    }
  },
  headerCard: {
    background: 'linear-gradient(135deg, rgba(15, 20, 35, 0.9) 0%, rgba(25, 32, 55, 0.9) 100%)',
    backdropFilter: 'blur(20px)',
    border: '1px solid',
    borderColor: alpha('#00d4ff', 0.2),
    borderRadius: 3,
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '2px',
      background: 'linear-gradient(90deg, transparent, #00d4ff, transparent)',
      animation: 'slideGlow 3s linear infinite'
    }
  },
  iconBox: {
    width: 64,
    height: 64,
    borderRadius: 2,
    background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.2) 0%, rgba(147, 51, 234, 0.2) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid',
    borderColor: alpha('#00d4ff', 0.3)
  },
  glassInput: {
    '& .MuiOutlinedInput-root': {
      background: alpha('#0f1419', 0.6),
      backdropFilter: 'blur(10px)',
      borderRadius: 2,
      '& fieldset': {
        borderColor: alpha('#00d4ff', 0.2),
      },
      '&:hover fieldset': {
        borderColor: alpha('#00d4ff', 0.4),
      },
      '&.Mui-focused fieldset': {
        borderColor: '#00d4ff',
      },
      '& input': {
        color: '#fff',
      }
    },
    '& .MuiInputLabel-root': {
      color: alpha('#00d4ff', 0.7),
    },
    '& .MuiSelect-select': {
      color: '#fff',
    },
    '& .MuiSvgIcon-root': {
      color: alpha('#00d4ff', 0.7),
    }
  },
  premiumChip: {
    borderColor: alpha('#00d4ff', 0.3),
    color: '#00d4ff',
    fontSize: '0.75rem',
    fontWeight: 600,
    '& .MuiChip-icon': {
      color: '#00d4ff',
      fontSize: '0.9rem'
    }
  },
  primaryButton: {
    background: 'linear-gradient(135deg, #00d4ff 0%, #7b2ff7 100%)',
    color: '#fff',
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: 2,
    boxShadow: '0 4px 20px rgba(0, 212, 255, 0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #00a8cc 0%, #6929c4 100%)',
      boxShadow: '0 6px 30px rgba(0, 212, 255, 0.4)',
      transform: 'translateY(-2px)',
    },
    '&:disabled': {
      background: alpha('#333', 0.5),
      color: alpha('#fff', 0.3),
    },
    transition: 'all 0.3s ease'
  },
  outlinedButton: {
    borderColor: alpha('#00d4ff', 0.5),
    color: '#00d4ff',
    '&:hover': {
      borderColor: '#00d4ff',
      background: alpha('#00d4ff', 0.1),
    }
  }
};

const colors = {
  primary: '#00d4ff',
  secondary: '#00ff88',
  danger: '#ff4757',
  warning: '#ffa502',
  gradients: {
    primary: 'linear-gradient(90deg, #00d4ff 0%, #7b2ff7 100%)',
    success: 'linear-gradient(90deg, #00ff88 0%, #00d4ff 100%)',
    danger: 'linear-gradient(90deg, #ff4757 0%, #ff6348 100%)'
  }
};

const mixins = {
  glassEffect: {
    background: alpha('#0f1419', 0.8),
    backdropFilter: 'blur(20px)',
    border: '1px solid',
    borderColor: alpha('#00d4ff', 0.1)
  }
};

// Timeframe categories with colors (but text will be white)
const timeframeCategories = [
  {
    label: 'SECONDS',
    color: '#00d4ff',
    options: [
      { value: '1s', label: '1 Second' },
      { value: '5s', label: '5 Seconds' },
      { value: '10s', label: '10 Seconds' },
      { value: '15s', label: '15 Seconds' },
      { value: '30s', label: '30 Seconds' }
    ]
  },
  {
    label: 'MINUTES',
    color: '#4285f4',
    options: [
      { value: '1m', label: '1 Minute' },
      { value: '3m', label: '3 Minutes' },
      { value: '5m', label: '5 Minutes' },
      { value: '15m', label: '15 Minutes' },
      { value: '30m', label: '30 Minutes' },
      { value: '45m', label: '45 Minutes' }
    ]
  },
  {
    label: 'HOURS',
    color: '#9333ea',
    options: [
      { value: '1h', label: '1 Hour' },
      { value: '2h', label: '2 Hours' },
      { value: '3h', label: '3 Hours' },
      { value: '4h', label: '4 Hours' }
    ]
  },
  {
    label: 'DAYS',
    color: '#10b981',
    options: [
      { value: '1D', label: '1 Day' },
      { value: '2D', label: '2 Days' },
      { value: '3D', label: '3 Days' }
    ]
  },
  {
    label: 'WEEKS',
    color: '#f59e0b',
    options: [
      { value: '1W', label: '1 Week' },
      { value: '2W', label: '2 Weeks' }
    ]
  },
  {
    label: 'MONTHS',
    color: '#ef4444',
    options: [
      { value: '1M', label: '1 Month' },
      { value: '3M', label: '3 Months' },
      { value: '6M', label: '6 Months' },
      { value: '12M', label: '12 Months' }
    ]
  }
];

const AVAILABLE_SIGNALS = [
  "COBRA_LONG_ACTIVE",
  "COBRA_SHORT_ACTIVE",
  "LUX_LONG_ACTIVE",
  "LUX_SHORT_ACTIVE",
  "LUX_LONG12_ACTIVE",
  "LUX_SHORT12_ACTIVE",
  "SMART_BAND_12_ACTIVE",
  "SMART_TRAIL_2_ACTIVE",
  "SMART_TRAIL_3_ACTIVE"
];

// Condition Builder Component
const ConditionBuilder = ({ conditionForm, onFieldChange, onAddCondition, currentConditionsCount }) => (
  <Paper
    elevation={0}
    sx={{
      ...mixins.glassEffect,
      p: 4,
      borderRadius: 3,
      border: '1px solid',
      borderColor: alpha(colors.primary, 0.2)
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
        Add New Condition
      </Typography>
      <Chip 
        label={`Condition ${currentConditionsCount + 1}`}
        sx={{ 
          bgcolor: alpha(colors.primary, 0.2),
          color: colors.primary,
          fontWeight: 600 
        }}
      />
    </Stack>
    
    <Alert severity="info" sx={{ mb: 3, bgcolor: alpha(colors.primary, 0.1), color: '#fff' }}>
      <Typography variant="caption">
        Note: The strategy will use the first condition for entry. Additional conditions can be used for advanced logic.
      </Typography>
    </Alert>
    
    <Grid container spacing={3}>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Condition ID"
          value={conditionForm.id}
          onChange={(e) => onFieldChange('id', e.target.value)}
          sx={styles.glassInput}
          placeholder="e.g., C1, C2"
          helperText="Unique identifier"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <TextField
          fullWidth
          label="Condition Name"
          value={conditionForm.name}
          onChange={(e) => onFieldChange('name', e.target.value)}
          sx={styles.glassInput}
          placeholder="e.g., Entry Condition"
          helperText="Descriptive name"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FormControl fullWidth sx={styles.glassInput}>
          <InputLabel>Condition Type</InputLabel>
          <Select
            value={conditionForm.type}
            onChange={(e) => onFieldChange('type', e.target.value)}
            label="Condition Type"
          >
            <MenuItem value="simultaneous">Simultaneous</MenuItem>
            <MenuItem value="timed">Timed Window</MenuItem>
            <MenuItem value="sequential_exclusion">Sequential Exclusion</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
    
    {/* Dynamic fields based on condition type */}
    {conditionForm.type === 'simultaneous' && (
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={styles.glassInput}>
            <InputLabel>Signal 1</InputLabel>
            <Select
              value={conditionForm.signal1}
              onChange={(e) => onFieldChange('signal1', e.target.value)}
              label="Signal 1"
            >
              {AVAILABLE_SIGNALS.map(signal => (
                <MenuItem key={signal} value={signal}>{signal}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={styles.glassInput}>
            <InputLabel>Signal 2</InputLabel>
            <Select
              value={conditionForm.signal2}
              onChange={(e) => onFieldChange('signal2', e.target.value)}
              label="Signal 2"
            >
              {AVAILABLE_SIGNALS.map(signal => (
                <MenuItem key={signal} value={signal}>{signal}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="number"
            label="Tolerance (seconds)"
            value={conditionForm.tolerance_seconds}
            onChange={(e) => onFieldChange('tolerance_seconds', parseInt(e.target.value) || 0)}
            sx={styles.glassInput}
          />
        </Grid>
      </Grid>
    )}
    
    {conditionForm.type === 'timed' && (
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth sx={styles.glassInput}>
            <InputLabel>Trigger Signal</InputLabel>
            <Select
              value={conditionForm.trigger_signal}
              onChange={(e) => onFieldChange('trigger_signal', e.target.value)}
              label="Trigger Signal"
            >
              {AVAILABLE_SIGNALS.map(signal => (
                <MenuItem key={signal} value={signal}>{signal}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth sx={styles.glassInput}>
            <InputLabel>Target Signal</InputLabel>
            <Select
              value={conditionForm.target_signal}
              onChange={(e) => onFieldChange('target_signal', e.target.value)}
              label="Target Signal"
            >
              {AVAILABLE_SIGNALS.map(signal => (
                <MenuItem key={signal} value={signal}>{signal}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            type="number"
            label="Min Minutes"
            value={conditionForm.min_minutes}
            onChange={(e) => onFieldChange('min_minutes', parseInt(e.target.value) || 0)}
            sx={styles.glassInput}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            type="number"
            label="Max Minutes"
            value={conditionForm.max_minutes}
            onChange={(e) => onFieldChange('max_minutes', parseInt(e.target.value) || 0)}
            sx={styles.glassInput}
          />
        </Grid>
      </Grid>
    )}
    
    {conditionForm.type === 'sequential_exclusion' && (
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={styles.glassInput}>
            <InputLabel>Early Signal</InputLabel>
            <Select
              value={conditionForm.early_signal}
              onChange={(e) => onFieldChange('early_signal', e.target.value)}
              label="Early Signal"
            >
              {AVAILABLE_SIGNALS.map(signal => (
                <MenuItem key={signal} value={signal}>{signal}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={styles.glassInput}>
            <InputLabel>Confirmation Signal</InputLabel>
            <Select
              value={conditionForm.confirmation_signal}
              onChange={(e) => onFieldChange('confirmation_signal', e.target.value)}
              label="Confirmation Signal"
            >
              {AVAILABLE_SIGNALS.map(signal => (
                <MenuItem key={signal} value={signal}>{signal}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={styles.glassInput}>
            <InputLabel>Excluded Signal</InputLabel>
            <Select
              value={conditionForm.excluded_signal}
              onChange={(e) => onFieldChange('excluded_signal', e.target.value)}
              label="Excluded Signal"
            >
              {AVAILABLE_SIGNALS.map(signal => (
                <MenuItem key={signal} value={signal}>{signal}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    )}
  </Paper>
);

// Condition Card Component
const ConditionCard = ({ condition, index, onRemove }) => (
  <Paper
    sx={{
      ...mixins.glassEffect,
      p: 3,
      borderRadius: 2,
      position: 'relative',
      overflow: 'hidden',
      border: '1px solid',
      borderColor: alpha(colors.secondary, 0.2),
      '&:hover': {
        borderColor: alpha(colors.secondary, 0.4),
        transform: 'translateX(4px)',
      },
      transition: 'all 0.3s ease'
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing={2}>
        <Chip 
          label={condition.id} 
          size="small"
          sx={{ 
            bgcolor: alpha(colors.secondary, 0.2),
            color: colors.secondary,
            fontWeight: 700 
          }}
        />
        <Typography variant="body1" sx={{ color: '#fff', fontWeight: 600 }}>
          {condition.name}
        </Typography>
        <Chip 
          label={condition.type} 
          size="small"
          variant="outlined"
          sx={{ 
            borderColor: alpha(colors.primary, 0.5),
            color: colors.primary 
          }}
        />
      </Stack>
      <IconButton 
        onClick={() => onRemove(index)}
        sx={{ color: colors.danger }}
      >
        <ClearIcon />
      </IconButton>
    </Stack>
    
    <Box sx={{ mt: 2 }}>
      {Object.entries(condition).map(([key, value]) => {
        if (key !== 'id' && key !== 'name' && key !== 'type' && value) {
          return (
            <Typography key={key} variant="caption" sx={{ color: alpha('#fff', 0.7), display: 'block' }}>
              {key.replace(/_/g, ' ')}: <span style={{ color: colors.primary }}>{value}</span>
            </Typography>
          );
        }
        return null;
      })}
    </Box>
  </Paper>
);

// Registered Strategy Card Component
const RegisteredStrategyCard = ({ strategy, onDelete, apiBaseUrl }) => {
  const handleDelete = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/strategies/api/strategy/delete/${strategy.strategy_id}/`, {
        method: 'DELETE'
      });
      if (response.ok) {
        onDelete();
      }
    } catch (error) {
      console.error('Failed to delete strategy:', error);
    }
  };

  // Helper function to get signal name from either string or object
  const getSignalName = (signal) => {
    if (!signal) return 'Not Set';
    if (typeof signal === 'string') return signal;
    if (typeof signal === 'object' && signal.name) return signal.name;
    return 'Unknown';
  };

  // Helper function to get timeframe from signal if it's an object
  const getSignalTimeframe = (signal) => {
    if (typeof signal === 'object' && signal.timeframe) return signal.timeframe;
    return null;
  };

  return (
    <Paper
      sx={{
        ...mixins.glassEffect,
        p: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: alpha(strategy.is_active ? colors.secondary : alpha('#fff', 0.2), 0.3),
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          borderColor: alpha(strategy.is_active ? colors.secondary : colors.primary, 0.5),
          transform: 'scale(1.02)',
        },
        transition: 'all 0.3s ease'
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              background: strategy.is_active 
                ? alpha(colors.secondary, 0.2)
                : alpha('#fff', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: strategy.is_active ? colors.secondary : alpha('#fff', 0.2)
            }}
          >
            <ChartIcon sx={{ color: strategy.is_active ? colors.secondary : '#fff' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
              {strategy.strategy_id || strategy.strategy_name}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Chip 
                label={strategy.symbol} 
                size="small"
                sx={{ 
                  bgcolor: alpha(colors.primary, 0.2),
                  color: colors.primary,
                  fontSize: '0.7rem'
                }}
              />
              <Chip 
                label={strategy.timeframe} 
                size="small"
                sx={{ 
                  bgcolor: alpha(colors.warning, 0.2),
                  color: colors.warning,
                  fontSize: '0.7rem'
                }}
              />
              {strategy.is_active && (
                <Chip 
                  label="ACTIVE" 
                  size="small"
                  sx={{ 
                    bgcolor: alpha(colors.secondary, 0.2),
                    color: colors.secondary,
                    fontSize: '0.7rem',
                    fontWeight: 700
                  }}
                />
              )}
            </Stack>
          </Box>
        </Stack>
        <IconButton onClick={handleDelete} sx={{ color: colors.danger }}>
          <ClearIcon />
        </IconButton>
      </Stack>
      
      {strategy.description && (
        <Typography variant="body2" sx={{ color: alpha('#fff', 0.7), mb: 2 }}>
          {strategy.description}
        </Typography>
      )}
      
      <Divider sx={{ borderColor: alpha('#fff', 0.1), my: 2 }} />
      
      <Stack spacing={1}>
        {strategy.early_signal && (
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
            Early Signal: <span style={{ color: colors.warning }}>
              {getSignalName(strategy.early_signal)}
              {getSignalTimeframe(strategy.early_signal) && (
                <span style={{ color: alpha('#fff', 0.4) }}> ({getSignalTimeframe(strategy.early_signal)})</span>
              )}
            </span>
          </Typography>
        )}
        {strategy.confirmation_signal && (
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
            Confirmation: <span style={{ color: colors.primary }}>
              {getSignalName(strategy.confirmation_signal)}
              {getSignalTimeframe(strategy.confirmation_signal) && (
                <span style={{ color: alpha('#fff', 0.4) }}> ({getSignalTimeframe(strategy.confirmation_signal)})</span>
              )}
            </span>
          </Typography>
        )}
        {strategy.exit_signal && (
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
            Exit Signal: <span style={{ color: colors.danger }}>
              {getSignalName(strategy.exit_signal)}
              {getSignalTimeframe(strategy.exit_signal) && (
                <span style={{ color: alpha('#fff', 0.4) }}> ({getSignalTimeframe(strategy.exit_signal)})</span>
              )}
            </span>
          </Typography>
        )}
        {(strategy.min_minutes !== undefined || strategy.max_minutes !== undefined) && (
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.5) }}>
            Time Window: <span style={{ color: colors.secondary }}>
              {strategy.min_minutes || 0} - {strategy.max_minutes || 60} minutes
            </span>
          </Typography>
        )}
        {strategy.created_at && (
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.3) }}>
            Created: {new Date(strategy.created_at).toLocaleDateString()}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

const StrategyRegistration = ({ apiBaseUrl = window.location.origin }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [registeredStrategies, setRegisteredStrategies] = useState([]);
  const [message, setMessage] = useState(null);
  const [showBuilder, setShowBuilder] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0 });
  
  // Strategy form state
  const [strategyForm, setStrategyForm] = useState({
    strategy_name: '',
    description: '',
    symbol: '',
    timeframe: '5m',
    type: 'LONG',
    exit_signal: '',
    conditions: []
  });
  
  // Condition form state
  const [conditionForm, setConditionForm] = useState({
    id: 'C1',
    name: 'Entry Condition 1',  // Default name
    type: 'simultaneous',
    signal1: '',
    signal2: '',
    tolerance_seconds: 60,  // Default 60 seconds tolerance
    trigger_signal: '',
    target_signal: '',
    min_minutes: 1,  // Default 1 minute minimum
    max_minutes: 10,  // Default 10 minutes maximum
    early_signal: '',
    confirmation_signal: '',
    excluded_signal: ''
  });

  // Find current timeframe category
  const currentCategory = timeframeCategories.find(cat => 
    cat.options.some(opt => opt.value === strategyForm.timeframe)
  );

  useEffect(() => {
    fetchRegisteredStrategies();
  }, []);

  const fetchRegisteredStrategies = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/strategies/api/strategy/list/`);
      if (response.ok) {
        const data = await response.json();
        setRegisteredStrategies(data.strategies || []);
        setStats({
          total: data.strategies?.length || 0,
          active: data.strategies?.filter(s => s.is_active).length || 0,
          pending: data.strategies?.filter(s => !s.is_active).length || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch strategies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStrategyFieldChange = (field, value) => {
    setStrategyForm(prev => ({ ...prev, [field]: value }));
  };

  const handleConditionFieldChange = (field, value) => {
    setConditionForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-update condition name based on type
      if (field === 'type') {
        const conditionNumber = strategyForm.conditions.length + 1;
        if (value === 'simultaneous') {
          updated.name = `Simultaneous Entry ${conditionNumber}`;
        } else if (value === 'timed') {
          updated.name = `Timed Window ${conditionNumber}`;
        } else if (value === 'sequential_exclusion') {
          updated.name = `Sequential Exclusion ${conditionNumber}`;
        }
      }
      
      return updated;
    });
  };

  const addCondition = () => {
    if (!conditionForm.id || !conditionForm.name) {
      setMessage({ type: 'error', text: 'Condition ID and Name are required' });
      return;
    }

    let newCondition = {
      id: conditionForm.id,
      name: conditionForm.name,
      type: conditionForm.type
    };

    // Add fields based on condition type
    switch (conditionForm.type) {
      case 'simultaneous':
        if (!conditionForm.signal1 || !conditionForm.signal2) {
          setMessage({ type: 'error', text: 'Both signals are required for simultaneous condition' });
          return;
        }
        newCondition = { 
          ...newCondition, 
          signal1: conditionForm.signal1, 
          signal2: conditionForm.signal2, 
          tolerance_seconds: conditionForm.tolerance_seconds 
        };
        break;
      case 'timed':
        if (!conditionForm.trigger_signal || !conditionForm.target_signal) {
          setMessage({ type: 'error', text: 'Trigger and target signals are required' });
          return;
        }
        newCondition = { 
          ...newCondition, 
          trigger_signal: conditionForm.trigger_signal, 
          target_signal: conditionForm.target_signal, 
          min_minutes: conditionForm.min_minutes, 
          max_minutes: conditionForm.max_minutes 
        };
        break;
      case 'sequential_exclusion':
        if (!conditionForm.early_signal || !conditionForm.confirmation_signal || !conditionForm.excluded_signal) {
          setMessage({ type: 'error', text: 'All signals are required for sequential exclusion' });
          return;
        }
        newCondition = { 
          ...newCondition, 
          early_signal: conditionForm.early_signal, 
          confirmation_signal: conditionForm.confirmation_signal, 
          excluded_signal: conditionForm.excluded_signal 
        };
        break;
    }

    setStrategyForm(prev => ({
      ...prev,
      conditions: [...prev.conditions, newCondition]
    }));

    // Reset condition form
    setConditionForm({
      id: `C${strategyForm.conditions.length + 2}`,
      name: `Entry Condition ${strategyForm.conditions.length + 2}`,  // Auto-generate name
      type: 'simultaneous',
      signal1: '',
      signal2: '',
      tolerance_seconds: 60,
      trigger_signal: '',
      target_signal: '',
      min_minutes: 1,
      max_minutes: 10,
      early_signal: '',
      confirmation_signal: '',
      excluded_signal: ''
    });
    
    setMessage({ type: 'success', text: 'Condition added successfully!' });
  };

  const removeCondition = (index) => {
    setStrategyForm(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index)
    }));
    setMessage({ type: 'info', text: 'Condition removed' });
  };

  const registerStrategy = async () => {
    if (!strategyForm.strategy_name || !strategyForm.exit_signal || !strategyForm.symbol) {
      setMessage({ type: 'error', text: 'Strategy name, symbol and exit signal are required' });
      return;
    }

    if (strategyForm.conditions.length === 0) {
      setMessage({ type: 'error', text: 'At least one condition is required' });
      return;
    }

    setSaving(true);
    try {
      // Transform the strategy form data to match backend expectations
      const firstCondition = strategyForm.conditions[0];
      
      let setupData = {
        strategy_id: strategyForm.strategy_name,
        symbol: strategyForm.symbol,
        timeframe: strategyForm.timeframe,
        exit_signal: strategyForm.exit_signal,
        min_minutes: 0,
        max_minutes: 60
      };

      // Map condition fields based on type
      if (firstCondition.type === 'timed') {
        setupData.early_signal = firstCondition.trigger_signal;
        setupData.confirmation_signal = firstCondition.target_signal;
        setupData.min_minutes = firstCondition.min_minutes || 1;
        setupData.max_minutes = firstCondition.max_minutes || 10;
      } else if (firstCondition.type === 'sequential_exclusion') {
        setupData.early_signal = firstCondition.early_signal;
        setupData.confirmation_signal = firstCondition.confirmation_signal;
        setupData.min_minutes = 1;
        setupData.max_minutes = 60;
      } else if (firstCondition.type === 'simultaneous') {
        setupData.early_signal = firstCondition.signal1;
        setupData.confirmation_signal = firstCondition.signal2;
        setupData.min_minutes = 0;
        setupData.max_minutes = Math.max(1, Math.ceil(firstCondition.tolerance_seconds / 60)); // Convert seconds to minutes
      }

      console.log('Registering strategy with data:', setupData);

      const response = await fetch(`${apiBaseUrl}/strategies/api/strategy/setup/`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(setupData)
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('Response error:', text);
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      if (data.success || response.ok) {
        setMessage({ 
          type: 'success', 
          text: `🚀 Strategy ${strategyForm.strategy_name} registered successfully!` 
        });
        fetchRegisteredStrategies();
        // Reset form
        setStrategyForm({
          strategy_name: '',
          description: '',
          symbol: '',
          timeframe: '5m',
          type: 'LONG',
          exit_signal: '',
          conditions: []
        });
        // Reset condition form
        setConditionForm({
          id: 'C1',
          name: 'Entry Condition 1',
          type: 'simultaneous',
          signal1: '',
          signal2: '',
          tolerance_seconds: 60,
          trigger_signal: '',
          target_signal: '',
          min_minutes: 1,
          max_minutes: 10,
          early_signal: '',
          confirmation_signal: '',
          excluded_signal: ''
        });
      } else {
        throw new Error(data.error || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to register strategy' 
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={styles.mainContainer}>
      <Box sx={{ position: 'relative', zIndex: 1, p: 3 }}>
        
        {/* Premium Header */}
        <Fade in timeout={500}>
          <Paper elevation={0} sx={{ ...styles.headerCard, p: 4, mb: 4 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Box sx={styles.iconBox}>
                    <RegisterIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        fontWeight: 800, 
                        color: '#fff',
                        letterSpacing: '-0.02em',
                        lineHeight: 1.2,
                        mb: 0.5
                      }}
                    >
                      Strategy Registration Hub
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: alpha('#00d4ff', 0.8),
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontWeight: 600
                      }}
                    >
                      Advanced Trading Strategy Builder • Real-time Condition Engine
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                  <Paper
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      background: alpha(colors.secondary, 0.1),
                      border: '1px solid',
                      borderColor: alpha(colors.secondary, 0.3),
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), fontSize: '0.7rem' }}>ACTIVE</Typography>
                    <Typography variant="h5" sx={{ color: colors.secondary, fontWeight: 700 }}>{stats.active}</Typography>
                  </Paper>
                  <Paper
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      background: alpha(colors.primary, 0.1),
                      border: '1px solid',
                      borderColor: alpha(colors.primary, 0.3),
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), fontSize: '0.7rem' }}>TOTAL</Typography>
                    <Typography variant="h5" sx={{ color: colors.primary, fontWeight: 700 }}>{stats.total}</Typography>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>

            {/* Alert Message */}
            <Collapse in={!!message}>
              <Alert 
                severity={message?.type}
                onClose={() => setMessage(null)}
                sx={{ 
                  mt: 3,
                  background: message?.type === 'success' ? alpha(colors.secondary, 0.1) :
                             message?.type === 'error' ? alpha(colors.danger, 0.1) :
                             alpha(colors.primary, 0.1),
                  color: '#fff',
                  '& .MuiAlert-icon': {
                    color: message?.type === 'success' ? colors.secondary :
                           message?.type === 'error' ? colors.danger :
                           colors.primary
                  }
                }}
              >
                {message?.text}
              </Alert>
            </Collapse>
          </Paper>
        </Fade>

        {/* Strategy Basic Info */}
        <Zoom in timeout={600}>
          <Paper
            elevation={0}
            sx={{
              ...mixins.glassEffect,
              p: 4,
              mb: 4,
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: colors.gradients.primary
              }
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '-0.01em' }}>
                Strategy Configuration
              </Typography>
              <Stack direction="row" spacing={1}>
                <Chip icon={<SparkleIcon />} label="AI Optimized" sx={styles.premiumChip} />
                <Chip icon={<ShieldIcon />} label="Secure" sx={{ ...styles.premiumChip, borderColor: alpha(colors.secondary, 0.3), color: colors.secondary }} />
              </Stack>
            </Stack>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Strategy Name"
                  placeholder="Enter unique strategy identifier..."
                  value={strategyForm.strategy_name}
                  onChange={(e) => handleStrategyFieldChange('strategy_name', e.target.value)}
                  sx={styles.glassInput}
                  InputProps={{
                    startAdornment: (
                      <RocketIcon sx={{ color: colors.primary, mr: 1.5 }} />
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Description"
                  placeholder="Brief strategy description..."
                  value={strategyForm.description}
                  onChange={(e) => handleStrategyFieldChange('description', e.target.value)}
                  sx={styles.glassInput}
                />
              </Grid>
              
              {/* Symbol as text input */}
              <Grid item xs={6} md={3}>
                <TextField
                  fullWidth
                  label="Symbol"
                  placeholder="e.g., BTCUSD, EURUSD"
                  value={strategyForm.symbol}
                  onChange={(e) => handleStrategyFieldChange('symbol', e.target.value.toUpperCase())}
                  sx={styles.glassInput}
                  InputProps={{
                    startAdornment: (
                      <ChartIcon sx={{ color: colors.primary, mr: 1 }} />
                    )
                  }}
                />
              </Grid>
              
              {/* Timeframe with categories */}
              <Grid item xs={6} md={3}>
                <FormControl fullWidth sx={styles.glassInput}>
                  <InputLabel>Timeframe</InputLabel>
                  <Select
                    value={strategyForm.timeframe}
                    onChange={(e) => handleStrategyFieldChange('timeframe', e.target.value)}
                    label="Timeframe"
                    renderValue={(value) => (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {currentCategory && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: currentCategory.color,
                              flexShrink: 0
                            }}
                          />
                        )}
                        <Typography sx={{ color: '#fff' }}>
                          {timeframeCategories
                            .flatMap(cat => cat.options)
                            .find(opt => opt.value === strategyForm.timeframe)?.label || strategyForm.timeframe}
                        </Typography>
                      </Stack>
                    )}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 400,
                          bgcolor: '#0f1419',
                          border: '1px solid',
                          borderColor: alpha(colors.primary, 0.2),
                          '& .MuiMenuItem-root': {
                            '&:hover': {
                              bgcolor: alpha(colors.primary, 0.1)
                            }
                          }
                        }
                      }
                    }}
                  >
                    {timeframeCategories.map((category) => [
                      <MenuItem disabled key={category.label}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#fff',
                            opacity: 0.7,
                            fontWeight: 600,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                            px: 2,
                            py: 1
                          }}
                        >
                          {category.label}
                        </Typography>
                      </MenuItem>,
                      ...category.options.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: category.color,
                                flexShrink: 0
                              }}
                            />
                            <Typography sx={{ color: '#fff' }}>
                              {opt.label}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    ])}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={6} md={3}>
                <FormControl fullWidth sx={styles.glassInput}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={strategyForm.type}
                    onChange={(e) => handleStrategyFieldChange('type', e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="LONG">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TrendingUpIcon sx={{ fontSize: 16, color: colors.secondary }} />
                        <span>LONG</span>
                      </Stack>
                    </MenuItem>
                    <MenuItem value="SHORT">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TrendingDownIcon sx={{ fontSize: 16, color: colors.danger }} />
                        <span>SHORT</span>
                      </Stack>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={6} md={3}>
                <FormControl fullWidth sx={styles.glassInput}>
                  <InputLabel>Exit Signal</InputLabel>
                  <Select
                    value={strategyForm.exit_signal}
                    onChange={(e) => handleStrategyFieldChange('exit_signal', e.target.value)}
                    label="Exit Signal"
                  >
                    <MenuItem value="">
                      <em>Select exit signal...</em>
                    </MenuItem>
                    {AVAILABLE_SIGNALS.map(signal => (
                      <MenuItem key={signal} value={signal}>
                        {signal}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        </Zoom>

        {/* Condition Builder Section */}
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
              Conditions Builder
            </Typography>
            <Button
              variant="outlined"
              startIcon={showBuilder ? <HideIcon /> : <ViewIcon />}
              onClick={() => setShowBuilder(!showBuilder)}
              sx={styles.outlinedButton}
            >
              {showBuilder ? 'Hide' : 'Show'} Builder
            </Button>
          </Stack>

          <Collapse in={showBuilder}>
            <ConditionBuilder
              conditionForm={conditionForm}
              onFieldChange={handleConditionFieldChange}
              onAddCondition={addCondition}
              currentConditionsCount={strategyForm.conditions.length}
            />
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={addCondition}
                sx={{
                  ...styles.primaryButton,
                  px: 4,
                  py: 1.5
                }}
              >
                Add Condition
              </Button>
            </Box>
          </Collapse>
        </Box>

        {/* Current Conditions */}
        {strategyForm.conditions.length > 0 && (
          <Fade in timeout={700}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 3 }}>
                Active Conditions ({strategyForm.conditions.length})
              </Typography>
              <Stack spacing={2}>
                {strategyForm.conditions.map((condition, index) => (
                  <ConditionCard
                    key={index}
                    condition={condition}
                    index={index}
                    onRemove={removeCondition}
                  />
                ))}
              </Stack>
            </Box>
          </Fade>
        )}

        {/* Register Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={saving ? null : <SaveIcon />}
            onClick={registerStrategy}
            disabled={saving || !strategyForm.strategy_name || !strategyForm.symbol || strategyForm.conditions.length === 0}
            sx={{
              ...styles.primaryButton,
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              minWidth: 250,
              position: 'relative'
            }}
          >
            {saving ? (
              <>
                <LinearProgress
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    borderRadius: '8px 8px 0 0'
                  }}
                />
                Registering...
              </>
            ) : (
              'Register Strategy'
            )}
          </Button>
        </Box>

        {/* Registered Strategies */}
        <Paper
          elevation={0}
          sx={{
            ...mixins.glassEffect,
            p: 4,
            borderRadius: 3
          }}
        >
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 3 }}>
            Registered Strategies
          </Typography>
          
          {loading ? (
            <Stack spacing={2}>
              {[1, 2, 3].map(i => (
                <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2, bgcolor: alpha('#fff', 0.05) }} />
              ))}
            </Stack>
          ) : registeredStrategies.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography sx={{ color: alpha('#fff', 0.5), fontSize: '1.1rem' }}>
                No strategies registered yet
              </Typography>
              <Typography sx={{ color: alpha('#fff', 0.3), mt: 1 }}>
                Create your first strategy above
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {registeredStrategies.map((strategy, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <RegisteredStrategyCard
                    strategy={strategy}
                    onDelete={fetchRegisteredStrategies}
                    apiBaseUrl={apiBaseUrl}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default StrategyRegistration;
